/**
 * Concept Dispatch — MUI theme on the Concept DESIGN STANDARD (2026-09-04).
 * Tokens mirror https://concept.bz/ui/concept-ui.css (navy / crimson, Refined fonts, button set, 6/12 px radii).
 * Ticket STATUS and PRIORITY colours are NOT here on purpose — they live in TicketsPage.tsx / tech/TechShared.tsx
 * and stay exactly as they were (Lyndon: "keep the coloration of the different events").
 */
import { createTheme } from '@mui/material/styles';

export const CC = {
  red: '#E30613', redDark: '#B00010', redLight: '#FF4A4A',
  navy: '#0D2233', navy2: '#123A54', navyLight: '#1E4A6B', blue: '#1C7ED6', blue2: '#14548C',
  teal: '#0CA678', ok: '#2F9E44', warn: '#F76707', err: '#C92A2A',
  line: '#DFE3E8', ink: '#1A2B3C', mut: '#6B7785', bg: '#F4F6F8', card: '#FFFFFF',
  fontHeading: '"Source Serif 4", Georgia, "Times New Roman", serif',
  fontBody: '"Source Sans 3", "Segoe UI", Roboto, Arial, sans-serif',
  fontMono: '"Source Code Pro", Consolas, monospace',
};
/** gradients from the standard button set */
export const GRAD = {
  primary: `linear-gradient(180deg, ${CC.navyLight}, ${CC.navy})`,
  primaryHover: `linear-gradient(180deg, ${CC.navy2}, #0A1A28)`,
  accent: `linear-gradient(180deg, ${CC.redLight}, ${CC.red} 55%, ${CC.redDark})`,
  accentHover: `linear-gradient(180deg, ${CC.red}, ${CC.redDark})`,
  success: `linear-gradient(180deg, #40C057, ${CC.ok})`,
  secondary: 'linear-gradient(180deg, #FFFFFF, #EDF0F3)',
  sidebar: `linear-gradient(180deg, ${CC.navy2} 0%, ${CC.navy} 100%)`,
  bar: `linear-gradient(90deg, ${CC.redLight}, ${CC.redDark})`,
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: CC.navy, light: CC.navyLight, dark: '#0A1A28', contrastText: '#fff' },
    secondary: { main: CC.red, light: CC.redLight, dark: CC.redDark, contrastText: '#fff' },
    success: { main: CC.ok, light: '#40C057', dark: '#2B8A3E' },
    warning: { main: CC.warn, light: '#FF922B', dark: '#D9480F' },
    error: { main: CC.err, light: '#F03E3E', dark: '#A61E1E' },
    info: { main: CC.blue, light: '#4DABF7', dark: CC.blue2 },
    background: { default: CC.bg, paper: CC.card },
    text: { primary: CC.ink, secondary: CC.mut, disabled: '#A0AAB4' },
    divider: CC.line,
  },
  typography: {
    fontFamily: CC.fontBody,
    h1: { fontFamily: CC.fontHeading, fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2, color: CC.navy },
    h2: { fontFamily: CC.fontHeading, fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.25, color: CC.navy },
    h3: { fontFamily: CC.fontHeading, fontSize: '1.625rem', fontWeight: 700, lineHeight: 1.3, color: CC.navy },
    h4: { fontFamily: CC.fontHeading, fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, color: CC.navy },
    h5: { fontFamily: CC.fontHeading, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.4, color: CC.navy },
    h6: { fontFamily: CC.fontHeading, fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.4, color: CC.navy },
    subtitle1: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.6 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.45 },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
    overline: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em' },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: CC.bg, color: CC.ink },
        '.cc-mono': { fontFamily: CC.fontMono },
        '::selection': { background: '#FFD8D8' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 6, minHeight: 36, padding: '6px 16px', fontSize: '0.875rem', fontWeight: 600,
          border: '1px solid transparent', boxShadow: 'none', transition: 'filter .15s, box-shadow .15s',
          '&:hover': { boxShadow: 'none', filter: 'brightness(1.04)' },
          '@media (max-width:768px)': { minHeight: 44, fontSize: '1rem' },
        },
        sizeSmall: { minHeight: 30, padding: '3px 12px', fontSize: '0.8125rem', '@media (max-width:768px)': { minHeight: 40, fontSize: '0.875rem' } },
        sizeLarge: { minHeight: 44, padding: '8px 22px', fontSize: '1rem', '@media (max-width:768px)': { minHeight: 52, fontSize: '1.0625rem' } },
        containedPrimary: {
          background: GRAD.primary, borderColor: '#0A1A28', color: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.18)',
          '&:hover': { background: GRAD.primaryHover, boxShadow: '0 1px 2px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.18)' },
        },
        containedSecondary: {
          background: GRAD.accent, borderColor: CC.redDark, color: '#fff',
          boxShadow: '0 2px 4px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.35)',
          '&:hover': { background: GRAD.accentHover, boxShadow: '0 2px 4px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.35)' },
        },
        containedSuccess: {
          background: GRAD.success, borderColor: '#2B8A3E', color: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,.2), inset 0 1px 0 rgba(255,255,255,.25)',
          '&:hover': { background: GRAD.success },
        },
        containedError: { background: GRAD.accent, borderColor: CC.redDark, '&:hover': { background: GRAD.accentHover } },
        containedWarning: { color: '#fff' },
        containedInherit: { background: GRAD.secondary, borderColor: '#C9D1DA', color: CC.ink, boxShadow: '0 1px 1px rgba(0,0,0,.08)' },
        outlined: { background: '#fff', borderWidth: 1.5, '&:hover': { background: '#F6F8FA' } },
        outlinedPrimary: { borderColor: '#C9D1DA', color: CC.ink, '&:hover': { borderColor: CC.navy, background: '#F6F8FA' } },
        outlinedSecondary: { borderColor: CC.red, color: CC.red, '&:hover': { background: '#FFF5F5', borderColor: CC.redDark } },
        text: { '&:hover': { background: '#EEF2F6' } },
        textPrimary: { color: CC.blue2 },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: 6 } } },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { borderRadius: 12, border: `1px solid ${CC.line}`, boxShadow: '0 1px 2px rgba(13,34,51,.05)', backgroundImage: 'none' } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
        outlined: { borderColor: CC.line },
        elevation1: { boxShadow: '0 1px 2px rgba(13,34,51,.06)' },
        elevation2: { boxShadow: '0 2px 6px rgba(13,34,51,.08)' },
        elevation3: { boxShadow: '0 4px 12px rgba(13,34,51,.10)' },
      },
    },
    MuiAppBar: { styleOverrides: { root: { background: CC.navy, color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.25)' } } },
    MuiDrawer: { styleOverrides: { paper: { borderRight: 'none', background: GRAD.sidebar, color: '#fff' } } },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 8, margin: '2px 8px', '&:hover': { background: 'rgba(255,255,255,.08)' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 20, fontWeight: 600, fontSize: '0.75rem' },
        sizeSmall: { height: 22 },
        outlined: { borderColor: CC.line },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { height: 3, borderRadius: '3px 3px 0 0', background: CC.red },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', minHeight: 44, color: CC.mut,
          '&.Mui-selected': { color: CC.navy },
          '@media (max-width:768px)': { minHeight: 48 },
        },
      },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 14, border: '1px solid #b9c3cc' } } },
    MuiDialogTitle: {
      styleOverrides: { root: { fontFamily: CC.fontHeading, fontWeight: 700, fontSize: '1.25rem', color: CC.navy, borderBottom: `1px solid ${CC.line}` } },
    },
    MuiDialogActions: { styleOverrides: { root: { borderTop: `1px solid ${CC.line}`, background: '#F9FAFB', padding: '12px 18px', gap: 8 } } },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6, background: '#fff',
          '& fieldset': { borderColor: '#C9D1DA' },
          '&:hover fieldset': { borderColor: CC.navyLight },
          '&.Mui-focused fieldset': { borderColor: CC.blue2, boxShadow: '0 0 0 3px rgba(20,84,140,.18)' },
        },
        notchedOutline: { borderColor: '#C9D1DA' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontWeight: 600, fontSize: '0.875rem' } } },
    MuiTableHead: {
      styleOverrides: {
        root: { '& .MuiTableCell-head': { background: '#F6F8FA', color: '#556', fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${CC.line}` } },
      },
    },
    MuiTableCell: { styleOverrides: { root: { borderBottom: `1px solid ${CC.line}`, fontSize: '0.875rem' } } },
    MuiTooltip: { styleOverrides: { tooltip: { background: CC.navy, fontSize: '0.75rem', borderRadius: 6 } } },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: { root: { border: `1px solid ${CC.line}`, borderRadius: '12px !important', '&:before': { display: 'none' }, marginBottom: 8 } },
    },
    MuiAccordionSummary: { styleOverrides: { root: { minHeight: 44, '& .MuiTypography-root': { color: CC.navy } } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiBottomNavigation: { styleOverrides: { root: { background: CC.navy, height: 64 } } },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,.72)', minWidth: 'auto', padding: '6px 0', borderRadius: 8, margin: '4px 2px',
          '&.Mui-selected': { color: '#fff', background: GRAD.accent, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.35)' },
        },
        label: { fontSize: '0.625rem', fontWeight: 600, '&.Mui-selected': { fontSize: '0.6875rem' } },
      },
    },
    MuiLinearProgress: { styleOverrides: { root: { borderRadius: 4, background: '#DEE7EF' }, bar: { background: GRAD.bar } } },
    MuiAvatar: { styleOverrides: { root: { fontFamily: CC.fontBody, fontWeight: 700 } } },
  },
});

export default theme;
